import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import CommonActionMenu from "../../components/common/CommonActionMenu";
import MainLayout from "../../components/layout/MainLayout";
import CommonTable from "../../components/common/CommonTable";
import api from "../../services/api";
import "./ViewAttendance.css";

function ViewAttendance() {
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [totalStats, setTotalStats] = useState({
    total_present: 0,
    total_absent: 0,
    total_leave: 0,
    total_halfday: 0,
    total_paidleave: 0,
    total_pending: 0,
    total_holiday: 0,
  });
  const [detailRow, setDetailRow] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [lightbox, setLightbox] = useState({
    visible: false,
    uri: "",
    label: "",
  });

  const getTodayDate = () => new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(getTodayDate());
  const [dateTo, setDateTo] = useState(getTodayDate());

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.post("/attendance/filter", {
        from_date: dateFrom,
        to_date: dateTo,
      });

      if (response.data.success) {
        setAttendanceData(response.data.data);
        setTotalStats(response.data.totalData);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load attendance data",
      });
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  const handleFilter = () => {
    if (new Date(dateFrom) > new Date(dateTo)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Date Range",
        text: "From date cannot be greater than To date",
      });
      return;
    }

    fetchAttendance();
  };

  const STATUS_CONFIG = {
    Present: { cls: "badge-success", icon: "fa-check-circle" },
    Absent: { cls: "badge-danger", icon: "fa-times-circle" },
    Leave: { cls: "badge-warning", icon: "fa-calendar" },
    "Half Day": { cls: "badge-info", icon: "fa-adjust" },
    "Paid Leave": { cls: "badge-primary", icon: "fa-gift" },
    Pending: { cls: "badge-pending", icon: "fa-clock" },
    Holiday: { cls: "badge-holiday", icon: "fa-umbrella-beach" },
  };

  const attendanceColumns = [
    {
      header: "#",
      id: "srno",
      render: (_, __, ___, meta) => meta.absoluteIndex + 1,
    },
    {
      header: "Emp. Code",
      key: "code",
      searchValue: (row) => row.code,
      render: (row) => <span className="va-emp-code">{row.code}</span>,
    },
    {
      header: "Employee",
      id: "employee",
      searchValue: (row) => row.name,
      render: (row) => {
        const name = row.name || "";
        const initials = name
          .split(" ")
          .slice(0, 2)
          .map((part) => part[0])
          .join("")
          .toUpperCase();
        const colors = [
          "#6557ff",
          "#10b981",
          "#f59e0b",
          "#ef4444",
          "#3b82f6",
          "#8b5cf6",
        ];
        const color = colors[name.charCodeAt(0) % colors.length] || colors[0];

        return (
          <div className="va-emp-cell">
            <div className="va-avatar" style={{ backgroundColor: color }}>
              {initials}
            </div>
            <div className="va-emp-meta">
              <span>{name}</span>
              {row.is_holiday ? (
                <span
                  className="va-holiday-pill"
                  title={(row.holiday_titles || []).join(", ")}
                >
                  <i className="fas fa-umbrella-beach"></i>{" "}
                  {(row.holiday_titles || []).join(", ") || "Holiday"}
                </span>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      header: "Date",
      key: "date",
      searchValue: (row) => row.date,
    },
    {
      header: "Clock In",
      key: "time_in",
      render: (row) => (
        <span className="va-time in">{row.time_in || "-"}</span>
      ),
    },
    {
      header: "Clock Out",
      key: "time_out",
      render: (row) => (
        <span className="va-time out">{row.time_out || "-"}</span>
      ),
    },
    {
      header: "Late",
      key: "late",
      searchValue: (row) => row.late,
      render: (row) =>
        row.late === "Yes" ? (
          <span className="va-late-tag">Late</span>
        ) : (
          <span className="va-on-time">On Time</span>
        ),
    },
    {
      header: "Overtime",
      key: "overtime",
      render: (row) =>
        row.overtime && row.overtime !== "00:00:00" ? (
          <span className="va-ot-tag">{row.overtime}</span>
        ) : (
          <span style={{ color: "#d1d5db" }}>-</span>
        ),
    },
    {
      header: "Status",
      key: "status",
      searchValue: (row) => row.status,
      render: (row) => {
        const config = STATUS_CONFIG[row.status] || {
          cls: "badge-secondary",
          icon: "fa-circle",
        };
        return (
          <div className="va-status-stack">
            <span className={`va-status-badge ${config.cls}`}>
              <i className={`fas ${config.icon}`}></i> {row.status}
            </span>
            {row.is_holiday && row.status !== "Holiday" ? (
              <span
                className="va-status-note"
                title={(row.holiday_titles || []).join(", ")}
              >
                Holiday override available
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      header: "Photos",
      id: "photos",
      render: (row) => {
        if (!row.photo_in && !row.photo_out) {
          return <span style={{ color: "#d1d5db" }}>-</span>;
        }

        return (
          <div className="va-photo-thumbs">
            {row.photo_in ? (
              <div
                className="va-thumb-wrap"
                onClick={(event) => {
                  event.stopPropagation();
                  setLightbox({
                    visible: true,
                    uri: row.photo_in,
                    label: `${row.name} · Clock In`,
                  });
                }}
              >
                <img src={row.photo_in} alt="In" className="va-thumb" />
                <span className="va-thumb-label in">In</span>
              </div>
            ) : null}
            {row.photo_out ? (
              <div
                className="va-thumb-wrap"
                onClick={(event) => {
                  event.stopPropagation();
                  setLightbox({
                    visible: true,
                    uri: row.photo_out,
                    label: `${row.name} · Clock Out`,
                  });
                }}
              >
                <img src={row.photo_out} alt="Out" className="va-thumb" />
                <span className="va-thumb-label out">Out</span>
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      header: "Location",
      id: "location",
      render: (row) =>
        row.latitude_in ? (
          <div className="va-coords">
            <i
              className="fas fa-map-marker-alt"
              style={{ color: "#6557ff" }}
            ></i>
            <span>{parseFloat(row.latitude_in).toFixed(4)},</span>
            <span>{parseFloat(row.longitude_in).toFixed(4)}</span>
          </div>
        ) : (
          <span style={{ color: "#d1d5db" }}>-</span>
        ),
    },
    {
      header: "",
      id: "actions",
      render: (row) => (
        <CommonActionMenu
          ariaLabel={`Actions for attendance ${row.code}`}
          actions={[
            {
              label: "View Details",
              icon: "fas fa-eye",
              onClick: () => {
                setDetailRow(row);
                setShowDetail(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  if (loading && attendanceData.length === 0) {
    return (
      <MainLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading attendance data...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="va-page">
        <div className="va-page-header">
          <h4 className="va-page-title">
            <i className="fas fa-finger-print"></i> View Attendance
          </h4>
        </div>

        <div className="va-stats-row">
          {[
            {
              label: "Present",
              value: totalStats.total_present,
              color: "#16a34a",
              bg: "#dcfce7",
              icon: "fa-check-circle",
            },
            {
              label: "Absent",
              value: totalStats.total_absent,
              color: "#dc2626",
              bg: "#fee2e2",
              icon: "fa-times-circle",
            },
            {
              label: "Half Day",
              value: totalStats.total_halfday,
              color: "#2563eb",
              bg: "#dbeafe",
              icon: "fa-adjust",
            },
            {
              label: "On Leave",
              value: totalStats.total_leave,
              color: "#ca8a04",
              bg: "#fef9c3",
              icon: "fa-calendar",
            },
            {
              label: "Paid Leave",
              value: totalStats.total_paidleave,
              color: "#7c3aed",
              bg: "#ede9fe",
              icon: "fa-gift",
            },
            {
              label: "Pending",
              value: totalStats.total_pending,
              color: "#6b7280",
              bg: "#f3f4f6",
              icon: "fa-clock",
            },
            {
              label: "Holiday",
              value: totalStats.total_holiday,
              color: "#b45309",
              bg: "#fef3c7",
              icon: "fa-umbrella-beach",
            },
          ].map((stat) => (
            <div
              className="va-stat-card"
              key={stat.label}
              style={{ backgroundColor: stat.bg }}
            >
              <i
                className={`fas ${stat.icon}`}
                style={{ color: stat.color, fontSize: 22 }}
              ></i>
              <span className="va-stat-value" style={{ color: stat.color }}>
                {stat.value}
              </span>
              <span className="va-stat-label">{stat.label}</span>
            </div>
          ))}
        </div>

        <div className="va-filter-card">
          <div className="va-filter-row">
            <div className="va-filter-group">
              <label>From</label>
              <div className="va-date-input">
                <i className="fas fa-calendar-alt"></i>
                <input
                  type="date"
                  value={dateFrom}
                  max={getTodayDate()}
                  onChange={(event) => setDateFrom(event.target.value)}
                />
              </div>
            </div>
            <i className="fas fa-arrow-right va-arrow"></i>
            <div className="va-filter-group">
              <label>To</label>
              <div className="va-date-input">
                <i className="fas fa-calendar-alt"></i>
                <input
                  type="date"
                  value={dateTo}
                  max={getTodayDate()}
                  onChange={(event) => setDateTo(event.target.value)}
                />
              </div>
            </div>
            <button
              className="va-search-btn"
              onClick={handleFilter}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="va-spinner-sm"></span> Loading...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i> Search
                </>
              )}
            </button>
          </div>
        </div>

        <div className="va-table-card">
          <CommonTable
            columns={attendanceColumns}
            data={attendanceData}
            tableClassName="va-table"
            wrapperClassName="va-table-wrap"
            noDataClassName="va-empty-cell"
            emptyContent={
              <>
                <i className="fas fa-calendar-times"></i>
                <p>No attendance records found</p>
              </>
            }
            getSearchText={(row) =>
              [
                row.code,
                row.name,
                row.date,
                row.time_in,
                row.time_out,
                row.status,
                row.late,
                row.overtime,
              ]
                .filter(Boolean)
                .join(" ")
            }
            enableSearch
            enablePagination
            searchPlaceholder="Search records..."
            toolbarClassName="va-toolbar"
            entriesClassName="va-show-entries"
            searchBoxClassName="va-search-box"
            searchClearClassName="va-search-clear"
            paginationClassName="va-pagination"
            paginationInfoClassName="va-pagination-info"
            paginationButtonsClassName="va-pagination-btns"
            paginationButtonClassName="va-page-btn"
            paginationEllipsisClassName="va-ellipsis"
            getRowClassName={(row) =>
              `va-row ${!row.has_record ? "va-row-no-record" : ""}`.trim()
            }
            onRowClick={(row) => {
              setDetailRow(row);
              setShowDetail(true);
            }}
          />
        </div>
      </div>

      {showDetail && detailRow ? (
        <div className="va-modal-overlay" onClick={() => setShowDetail(false)}>
          <div
            className="va-detail-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="va-modal-header">
              <h5>
                <i className="fas fa-id-card"></i> Attendance Detail
              </h5>
              <button
                className="va-modal-close"
                onClick={() => setShowDetail(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {(() => {
              const config = STATUS_CONFIG[detailRow.status] || {
                cls: "badge-secondary",
                icon: "fa-circle",
              };
              return (
                <div className={`va-status-hero ${config.cls}`}>
                  <i className={`fas ${config.icon}`}></i>
                  <span>{detailRow.status}</span>
                </div>
              );
            })()}

            <div className="va-detail-grid">
              {[
                { icon: "fa-user", label: "Name", value: detailRow.name },
                { icon: "fa-id-badge", label: "Emp Code", value: detailRow.code },
                { icon: "fa-calendar", label: "Date", value: detailRow.date },
                {
                  icon: "fa-sign-in-alt",
                  label: "Clock In",
                  value: detailRow.time_in || "-",
                  color: "#16a34a",
                },
                {
                  icon: "fa-sign-out-alt",
                  label: "Clock Out",
                  value: detailRow.time_out || "-",
                  color: "#dc2626",
                },
                {
                  icon: "fa-clock",
                  label: "Late",
                  value: detailRow.late,
                  color: detailRow.late === "Yes" ? "#dc2626" : "#16a34a",
                },
                {
                  icon: "fa-history",
                  label: "Overtime",
                  value:
                    detailRow.overtime !== "00:00:00"
                      ? detailRow.overtime
                      : "-",
                  color: "#6557ff",
                },
                {
                  icon: "fa-umbrella-beach",
                  label: "Holiday",
                  value:
                    detailRow.holiday_titles?.length > 0
                      ? detailRow.holiday_titles.join(", ")
                      : "No",
                  color: detailRow.holiday_titles?.length ? "#b45309" : "#6b7280",
                },
              ].map((row) => (
                <div className="va-detail-row" key={row.label}>
                  <div className="va-detail-icon-wrap">
                    <i className={`fas ${row.icon}`}></i>
                  </div>
                  <span className="va-detail-label">{row.label}</span>
                  <span
                    className="va-detail-value"
                    style={row.color ? { color: row.color } : {}}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            {detailRow.latitude_in ? (
              <div className="va-detail-section">
                <div className="va-detail-section-title">
                  <i className="fas fa-map-marker-alt"></i> Location
                </div>
                <div className="va-location-grid">
                  <div className="va-location-item">
                    <span className="va-location-label">
                      <i
                        className="fas fa-sign-in-alt"
                        style={{ color: "#16a34a" }}
                      ></i>{" "}
                      Clock In
                    </span>
                    <span className="va-location-coords">
                      {parseFloat(detailRow.latitude_in).toFixed(5)},{" "}
                      {parseFloat(detailRow.longitude_in).toFixed(5)}
                    </span>
                  </div>
                  {detailRow.latitude_out ? (
                    <div className="va-location-item">
                      <span className="va-location-label">
                        <i
                          className="fas fa-sign-out-alt"
                          style={{ color: "#dc2626" }}
                        ></i>{" "}
                        Clock Out
                      </span>
                      <span className="va-location-coords">
                        {parseFloat(detailRow.latitude_out).toFixed(5)},{" "}
                        {parseFloat(detailRow.longitude_out).toFixed(5)}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {detailRow.photo_in || detailRow.photo_out ? (
              <div className="va-detail-section">
                <div className="va-detail-section-title">
                  <i className="fas fa-camera"></i> Attendance Photos
                </div>
                <div className="va-detail-photos">
                  {detailRow.photo_in ? (
                    <div
                      className="va-detail-photo-wrap"
                      onClick={() => {
                        setShowDetail(false);
                        setTimeout(
                          () =>
                            setLightbox({
                              visible: true,
                              uri: detailRow.photo_in,
                              label: `${detailRow.name} · Clock In`,
                            }),
                          300,
                        );
                      }}
                    >
                      <img src={detailRow.photo_in} alt="Clock In" />
                      <div className="va-detail-photo-label in">
                        <i className="fas fa-sign-in-alt"></i> Clock In
                      </div>
                    </div>
                  ) : null}
                  {detailRow.photo_out ? (
                    <div
                      className="va-detail-photo-wrap"
                      onClick={() => {
                        setShowDetail(false);
                        setTimeout(
                          () =>
                            setLightbox({
                              visible: true,
                              uri: detailRow.photo_out,
                              label: `${detailRow.name} · Clock Out`,
                            }),
                          300,
                        );
                      }}
                    >
                      <img src={detailRow.photo_out} alt="Clock Out" />
                      <div className="va-detail-photo-label out">
                        <i className="fas fa-sign-out-alt"></i> Clock Out
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {lightbox.visible ? (
        <div
          className="va-lightbox"
          onClick={() => setLightbox({ visible: false, uri: "", label: "" })}
        >
          <button className="va-lightbox-close">
            <i className="fas fa-times"></i>
          </button>
          <p className="va-lightbox-label">{lightbox.label}</p>
          <img
            src={lightbox.uri}
            alt={lightbox.label}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}
    </MainLayout>
  );
}

export default ViewAttendance;
